// Monetary scores out of a VERSIONED meetstandaard document, keyed by the same
// MSIId the effect picker persists when the effect is adopted.
//
// Why this exists: the monetisation stream (useMsiMonetaryScores / the nightly
// fetchMsiScores) only ever read the legacy /database catalogue, which by the
// api team's confirmation will NEVER contain energiearmoede — the two pipelines
// are separate. So every adopted energiearmoede effect had an MSIId that was
// absent from the score map, got flagged `noScores`, and contributed €0 to both
// the tenant card and the nightly platform total. Silently: a €0 effect looks
// exactly like an effect nobody answered yet.
//
// The id and the score shape MUST match adaptMeetstandaardDocument — the picker
// writes the MSIId, this reads it back — so both come from that module rather
// than being re-derived here.
//
// AI-CONTEXT: See docs/features/msi-effect-monetisation.md
import { effectUidOf, scoresFrom } from './adaptMeetstandaardDocument'
import { canonicalStandaardVersion } from './standaardVersions'

/**
 * @typedef {object} MsiScoreLevel
 * @property {number} score - Discrete score level (1..5)
 * @property {number} monetaryValue - Yearly societal value in euros (can be negative)
 */

/**
 * Flattens one versioned document to a map of `MSIId@version` → scores[],
 * keeping only effects that actually carry monetary levels.
 *
 * Effects with no statements (co2-reductie, arbeidsparticipatie) are kept when
 * they have levels: they can never be adopted, so the entry is simply never
 * looked up — filtering them out here would just be a second, divergent rule
 * about what "adoptable" means.
 *
 * @param {object|null|undefined} doc - A versioned meetstandaard document
 * @returns {Object<string, Array<MsiScoreLevel>>}
 */
const buildScoresFromStandaardDocument = (doc) => {
    /** @type {Object<string, Array<MsiScoreLevel>>} */
    const map = {}
    const sector = doc?.meta?.sector || ''
    const version = doc?.meta?.version || ''
    if (!sector || !version) return map

    const effecten = Array.isArray(doc?.effecten) ? doc.effecten : []
    effecten.forEach((effect) => {
        const slug = effect?.slug
        if (!slug) return
        const scores = scoresFrom(effect.monetarisering)
        if (scores.length === 0) return
        const id = versionedScoreKey(effectUidOf(effect, sector), version)
        // First entry wins, same rule as the legacy flattener.
        if (map[id]) return
        map[id] = scores
    })
    return map
}

/**
 * Merges the legacy score map with the versioned ones into the single map the
 * monetisation helpers look effects up in.
 *
 * The two keyspaces are disjoint in practice — legacy ids are uuids, versioned
 * ids are `sector:slug@version` — so the precedence never actually triggers today.
 * Versioned wins on a collision anyway: it is the pipeline the legacy catalogue
 * is being retired into, so if an id ever appears in both, the versioned
 * document is the newer authority.
 *
 * @param {Object<string, Array<MsiScoreLevel>>|null|undefined} legacyMap
 * @param {Array<Object<string, Array<MsiScoreLevel>>>|null|undefined} versionedMaps
 * @returns {Object<string, Array<MsiScoreLevel>>}
 */
const mergeScoreMaps = (legacyMap, versionedMaps) => {
    const merged = { ...(legacyMap || {}) }
    ;(Array.isArray(versionedMaps) ? versionedMaps : []).forEach((map) => {
        Object.assign(merged, map || {})
    })
    return merged
}

/**
 * Key of one effect's scores within one version: `energiearmoede:slug@0.9`.
 *
 * The version is part of the key because the same effect carries different
 * amounts per version (0.10 moved to price level 2025), and an effect adopted
 * under 0.9 keeps 0.9's amounts — no retroactive recalculation.
 *
 * @param {string} msiId
 * @param {string} version
 * @returns {string}
 */
const versionedScoreKey = (msiId, version) => `${msiId}@${version}`

/**
 * Whether any version of this effect has amounts. Used before an effect's rows
 * are loaded, to decide whether loading them can produce euros at all.
 *
 * @param {Object<string, Array<object>>|null|undefined} scoresByMsiId
 * @param {string} msiId
 * @returns {boolean}
 */
const hasScoresFor = (scoresByMsiId, msiId) => {
    if (!scoresByMsiId || !msiId) return false
    if (Object.prototype.hasOwnProperty.call(scoresByMsiId, msiId)) return true
    const prefix = `${msiId}@`
    return Object.keys(scoresByMsiId).some((key) => key.startsWith(prefix))
}

/**
 * The scores of one adopted effect, in the version its rows were adopted under
 * — or the version that one counts as (EQUIVALENT_STANDAARD_VERSIONS).
 *
 * Legacy effects are keyed by their bare MSIId. A versioned effect takes its
 * version from the StandaardVersion stamped on its MatrixQuestionRows — the
 * OutputEffects doc does not reliably carry one. No version, or more than one,
 * returns no scores with the reason: a guessed version would put a figure on
 * screen that traces to the wrong document.
 *
 * @param {Object<string, Array<object>>|null|undefined} scoresByMsiId
 * @param {string} msiId
 * @param {Array<{StandaardVersion?: string}>|null|undefined} rowDefs - the effect's MatrixQuestionRows
 * @returns {{scores: Array<object>|null, standaardVersion: string|null, versionProblem: string|null}}
 */
const scoresForEffect = (scoresByMsiId, msiId, rowDefs) => {
    const map = scoresByMsiId || {}
    if (Object.prototype.hasOwnProperty.call(map, msiId)) {
        return { scores: map[msiId], standaardVersion: null, versionProblem: null }
    }
    if (!hasScoresFor(map, msiId)) return { scores: null, standaardVersion: null, versionProblem: null }

    // Equivalent versions count as one: a 0.9 row is valued at 0.10 once the
    // two are declared the same statements (see EQUIVALENT_STANDAARD_VERSIONS).
    const sector = String(msiId).split(':')[0]
    const versions = [...new Set((Array.isArray(rowDefs) ? rowDefs : [])
        .map((row) => row && row.StandaardVersion)
        .filter(Boolean)
        .map((version) => canonicalStandaardVersion(sector, version)))]
    if (versions.length === 0) return { scores: null, standaardVersion: null, versionProblem: 'geen-versie' }
    if (versions.length > 1) return { scores: null, standaardVersion: null, versionProblem: 'gemengde-versies' }

    const scores = map[versionedScoreKey(msiId, versions[0])] || null
    return { scores, standaardVersion: versions[0], versionProblem: scores ? null : 'onbekende-versie' }
}

export default buildScoresFromStandaardDocument
export { mergeScoreMaps, versionedScoreKey, hasScoresFor, scoresForEffect }
